/**
 * LPA Questions API
 *
 * CRUD operations for `core.mod_lpa_questions` and tag assignments via
 * `core.mod_lpa_question_tag_assignments`.
 *
 * Questions are factory-scoped (tenant_id + factory_id).
 * Tag assignment sync is a full-replace strategy: provide desired tag_ids
 * and the function reconciles the junction table.
 */

import { supabase } from '@/integrations/supabase/client';
import type {
  LpaQuestion,
  LpaQuestionWithTags,
  LpaTag,
  CreateQuestionInput,
  UpdateQuestionInput,
} from '../types';

// ---------------------------------------------------------------------------
// Internal helpers
// ---------------------------------------------------------------------------

/**
 * Resolves tags for a set of question IDs in a single query.
 * Returns a map of questionId -> LpaTag[].
 */
async function resolveTagsForQuestions(
  questionIds: string[],
): Promise<Record<string, LpaTag[]>> {
  if (questionIds.length === 0) return {};

  const { data: assignments, error: aErr } = await supabase
    .schema('core')
    .from('mod_lpa_question_tag_assignments')
    .select('question_id, tag_id')
    .in('question_id', questionIds);

  if (aErr) throw aErr;
  if (!assignments || assignments.length === 0) return {};

  const tagIds = [...new Set(assignments.map((a) => a.tag_id))];

  const { data: tags, error: tErr } = await supabase
    .schema('core')
    .from('mod_lpa_tags')
    .select('*')
    .in('id', tagIds);

  if (tErr) throw tErr;

  const tagMap = new Map((tags ?? []).map((t) => [t.id, t]));
  const result: Record<string, LpaTag[]> = {};

  for (const a of assignments) {
    const tag = tagMap.get(a.tag_id);
    if (tag) {
      (result[a.question_id] ??= []).push(tag as unknown as LpaTag);
    }
  }
  return result;
}

/**
 * Syncs tag assignments for a question. Full-replace strategy:
 * deletes old assignments, inserts new ones.
 */
async function syncTagAssignments(
  questionId: string,
  tenantId: string,
  tagIds: string[],
): Promise<void> {
  // Delete existing assignments for this question
  const { error: delErr } = await supabase
    .schema('core')
    .from('mod_lpa_question_tag_assignments')
    .delete()
    .eq('question_id', questionId);

  if (delErr) throw delErr;

  if (tagIds.length === 0) return;

  // Insert new assignments
  const rows = tagIds.map((tagId) => ({
    tenant_id: tenantId,
    question_id: questionId,
    tag_id: tagId,
  }));

  const { error: insErr } = await supabase
    .schema('core')
    .from('mod_lpa_question_tag_assignments')
    .insert(rows);

  if (insErr) throw insErr;
}

// ---------------------------------------------------------------------------
// Queries
// ---------------------------------------------------------------------------

/** Fetch all questions for a factory, with their tags resolved. */
export async function fetchQuestions(
  factoryId: string,
): Promise<LpaQuestionWithTags[]> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_questions')
    .select('*')
    .eq('factory_id', factoryId)
    .order('sort_order')
    .order('created_at');

  if (error) throw error;
  const questions = (data ?? []) as unknown as LpaQuestion[];

  const tagMap = await resolveTagsForQuestions(questions.map((q) => q.id));

  return questions.map((q) => ({
    ...q,
    tags: tagMap[q.id] ?? [],
  }));
}

/** Fetch a single question by ID, with tags. */
export async function fetchQuestionById(
  questionId: string,
): Promise<LpaQuestionWithTags | null> {
  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_questions')
    .select('*')
    .eq('id', questionId)
    .maybeSingle();

  if (error) throw error;
  if (!data) return null;

  const question = data as unknown as LpaQuestion;
  const tagMap = await resolveTagsForQuestions([question.id]);

  return { ...question, tags: tagMap[question.id] ?? [] };
}

// ---------------------------------------------------------------------------
// Mutations
// ---------------------------------------------------------------------------

/** Create a new question and optionally assign tags. */
export async function createQuestion(
  input: CreateQuestionInput,
): Promise<LpaQuestionWithTags> {
  const { tag_ids, ...questionFields } = input;

  const { data, error } = await supabase
    .schema('core')
    .from('mod_lpa_questions')
    .insert({
      tenant_id: questionFields.tenant_id,
      factory_id: questionFields.factory_id,
      question: questionFields.question,
      question_translations: questionFields.question_translations ?? {},
      explanation: questionFields.explanation ?? null,
      explanation_translations: questionFields.explanation_translations ?? {},
      reaction_plan: questionFields.reaction_plan ?? null,
      reaction_plan_translations: questionFields.reaction_plan_translations ?? {},
      estimated_duration_seconds: questionFields.estimated_duration_seconds ?? null,
      sort_order: questionFields.sort_order ?? 0,
      created_by: questionFields.created_by ?? null,
    })
    .select()
    .single();

  if (error) throw error;
  const question = data as unknown as LpaQuestion;

  if (tag_ids && tag_ids.length > 0) {
    await syncTagAssignments(question.id, question.tenant_id, tag_ids);
  }

  const tagMap = await resolveTagsForQuestions([question.id]);
  return { ...question, tags: tagMap[question.id] ?? [] };
}

/** Update a question. If tag_ids is provided, syncs tag assignments. */
export async function updateQuestion(
  questionId: string,
  input: UpdateQuestionInput,
): Promise<LpaQuestionWithTags> {
  const { tag_ids, ...updateFields } = input;

  let question: LpaQuestion;

  // When only tag_ids are provided, avoid UPDATE {} which can produce
  // "Cannot coerce the result to a single JSON object" in PostgREST.
  if (Object.keys(updateFields).length > 0) {
    const { data, error } = await supabase
      .schema('core')
      .from('mod_lpa_questions')
      .update(updateFields)
      .eq('id', questionId)
      .select()
      .single();

    if (error) throw error;
    question = data as unknown as LpaQuestion;
  } else {
    const { data, error } = await supabase
      .schema('core')
      .from('mod_lpa_questions')
      .select('*')
      .eq('id', questionId)
      .single();

    if (error) throw error;
    question = data as unknown as LpaQuestion;
  }

  if (tag_ids !== undefined) {
    await syncTagAssignments(question.id, question.tenant_id, tag_ids);
  }

  const tagMap = await resolveTagsForQuestions([question.id]);
  return { ...question, tags: tagMap[question.id] ?? [] };
}

/** Delete a question. Cascade-deletes tag assignments. */
export async function deleteQuestion(questionId: string): Promise<void> {
  const { error } = await supabase
    .schema('core')
    .from('mod_lpa_questions')
    .delete()
    .eq('id', questionId);

  if (error) throw error;
}
